<div class="page-header mb-3">
  <h2 class="page-title">
    <nb-icon icon="grid-outline" style="margin-right: 8px;"></nb-icon>
    Chain Catalog
  </h2>
  <p class="page-subtitle text-muted" *ngIf="chainId">Products and categories shared across all stores in this chain</p>
</div>

<nb-tabset>

  <!-- ═══════════════════════════════════════════════════════
       TAB 1 — CATEGORIES
  ════════════════════════════════════════════════════════ -->
  <nb-tab tabTitle="Categories" tabIcon="folder-outline">

    <!-- Add / Edit Category inline form -->
    <div class="form-panel" *ngIf="showCategoryForm">
      <nb-card class="form-card">
        <nb-card-header>
          <span>{{ editingCategory ? 'Edit Category' : 'Add Category' }}</span>
        </nb-card-header>
        <nb-card-body>
          <form [formGroup]="categoryForm" (ngSubmit)="submitCategoryForm()">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Name <span class="text-danger">*</span></label>
                <input
                  nbInput
                  fullWidth
                  formControlName="name"
                  placeholder="Category name"
                  (input)="onCategoryNameChange()"
                  [status]="categoryForm.get('name')?.invalid && categoryForm.get('name')?.touched ? 'danger' : 'basic'"
                />
                <div class="error-msg" *ngIf="categoryForm.get('name')?.invalid && categoryForm.get('name')?.touched">
                  Name is required (min 2 characters)
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Slug</label>
                <input
                  nbInput
                  fullWidth
                  formControlName="slug"
                  placeholder="auto-generated-slug"
                />
              </div>
              <div class="col-md-8 mb-3">
                <label class="form-label">Description</label>
                <input
                  nbInput
                  fullWidth
                  formControlName="description"
                  placeholder="Optional description"
                />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Sort Order</label>
                <input
                  nbInput
                  fullWidth
                  type="number"
                  formControlName="sortOrder"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
            <div class="form-actions">
              <button nbButton status="primary" type="submit" [disabled]="categoryForm.invalid">
                {{ editingCategory ? 'Update Category' : 'Create Category' }}
              </button>
              <button nbButton status="basic" type="button" class="ms-2" (click)="cancelCategoryForm()">
                Cancel
              </button>
            </div>
          </form>
        </nb-card-body>
      </nb-card>
    </div>

    <!-- Toolbar -->
    <div class="tab-toolbar d-flex justify-content-between align-items-center mb-3" *ngIf="!showCategoryForm">
      <span class="record-count text-muted">{{ categories.length }} category(ies)</span>
      <button nbButton status="primary" size="small" (click)="openAddCategory()">
        <nb-icon icon="plus-outline"></nb-icon> Add Category
      </button>
    </div>

    <!-- Categories table -->
    <div class="table-responsive" *ngIf="!categoryLoading">
      <table class="table table-hover text-center" *ngIf="categories.length > 0">
        <thead class="table-light">
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Description</th>
            <th>Sort</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let cat of categories">
            <td class="fw-semibold">{{ cat.name }}</td>
            <td><code class="slug-code">{{ cat.slug }}</code></td>
            <td class="text-muted small">{{ cat.description || '—' }}</td>
            <td>{{ cat.sortOrder ?? '—' }}</td>
            <td>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm me-1"
                (click)="openEditCategory(cat)"
                title="Edit"
              >
                <nb-icon icon="edit-outline"></nb-icon>
              </button>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm"
                (click)="deleteCategory(cat)"
                title="Delete"
              >
                <nb-icon icon="trash-2-outline"></nb-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="empty-state" *ngIf="categories.length === 0">
        <nb-card>
          <nb-card-body class="text-center py-5">
            <nb-icon icon="folder-outline" class="empty-icon"></nb-icon>
            <p class="text-muted mt-3">No categories yet.</p>
            <button nbButton status="primary" size="small" (click)="openAddCategory()">
              <nb-icon icon="plus-outline"></nb-icon> Create First Category
            </button>
          </nb-card-body>
        </nb-card>
      </div>
    </div>

    <div class="text-center py-4" *ngIf="categoryLoading">
      <nb-icon icon="loader-outline" class="spin-icon"></nb-icon>
      <span class="ms-2 text-muted">Loading categories…</span>
    </div>
  </nb-tab>

  <!-- ═══════════════════════════════════════════════════════
       TAB 2 — PRODUCTS
  ════════════════════════════════════════════════════════ -->
  <nb-tab tabTitle="Products" tabIcon="shopping-bag-outline">

    <!-- Add / Edit Product inline form -->
    <div class="form-panel" *ngIf="showProductForm">
      <nb-card class="form-card">
        <nb-card-header>
          <span>{{ editingProduct ? 'Edit Product' : 'Add Product' }}</span>
        </nb-card-header>
        <nb-card-body>
          <form [formGroup]="productForm" (ngSubmit)="submitProductForm()">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Name <span class="text-danger">*</span></label>
                <input
                  nbInput
                  fullWidth
                  formControlName="name"
                  placeholder="Product name"
                  [status]="productForm.get('name')?.invalid && productForm.get('name')?.touched ? 'danger' : 'basic'"
                />
                <div class="error-msg" *ngIf="productForm.get('name')?.invalid && productForm.get('name')?.touched">
                  Name is required (min 2 characters)
                </div>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Price <span class="text-danger">*</span></label>
                <input
                  nbInput
                  fullWidth
                  type="number"
                  step="0.01"
                  min="0"
                  formControlName="price"
                  placeholder="0.00"
                  [status]="productForm.get('price')?.invalid && productForm.get('price')?.touched ? 'danger' : 'basic'"
                />
                <div class="error-msg" *ngIf="productForm.get('price')?.invalid && productForm.get('price')?.touched">
                  Valid price is required
                </div>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Barcode (EAN)</label>
                <input
                  nbInput
                  fullWidth
                  formControlName="barcode"
                  placeholder="Optional barcode"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Category</label>
                <select
                  class="form-select"
                  formControlName="categoryId"
                >
                  <option value="">— No category —</option>
                  <option *ngFor="let cat of categories" [value]="cat._id">{{ cat.name }}</option>
                </select>
              </div>
              <div class="col-md-6 mb-3 d-flex align-items-center" style="padding-top: 28px;">
                <nb-checkbox formControlName="isVisible">Visible in stores</nb-checkbox>
              </div>
              <div class="col-12 mb-3">
                <label class="form-label">Description</label>
                <textarea
                  nbInput
                  fullWidth
                  rows="2"
                  formControlName="description"
                  placeholder="Optional description"
                ></textarea>
              </div>
            </div>
            <div class="form-actions">
              <button nbButton status="primary" type="submit" [disabled]="productForm.invalid">
                {{ editingProduct ? 'Update Product' : 'Create Product' }}
              </button>
              <button nbButton status="basic" type="button" class="ms-2" (click)="cancelProductForm()">
                Cancel
              </button>
            </div>
          </form>
        </nb-card-body>
      </nb-card>
    </div>

    <!-- Toolbar -->
    <div class="tab-toolbar d-flex justify-content-between align-items-center mb-3" *ngIf="!showProductForm">
      <span class="record-count text-muted">{{ products.length }} product(s)</span>
      <button nbButton status="primary" size="small" (click)="openAddProduct()">
        <nb-icon icon="plus-outline"></nb-icon> Add Product
      </button>
    </div>

    <!-- Products table -->
    <div class="table-responsive" *ngIf="!productLoading">
      <table class="table table-hover text-center" *ngIf="products.length > 0">
        <thead class="table-light">
          <tr>
            <th>Name</th>
            <th>Barcode</th>
            <th>Price</th>
            <th>Category</th>
            <th>Visibility</th>
            <th>Scope</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let product of products">
            <td class="fw-semibold">{{ product.name }}</td>
            <td class="text-muted small">{{ product.barcode || '—' }}</td>
            <td>{{ product.price | number:'1.2-2' }}</td>
            <td>{{ getCategoryName(product.categoryId) }}</td>
            <td>
              <nb-badge
                [text]="product.isVisible !== false ? 'Visible' : 'Hidden'"
                [status]="product.isVisible !== false ? 'success' : 'basic'"
              ></nb-badge>
            </td>
            <td>
              <span class="badge chain-scope-badge">
                <nb-icon icon="link-2-outline" style="font-size: 11px; vertical-align: middle;"></nb-icon>
                Shared across all stores
              </span>
            </td>
            <td>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm me-1"
                (click)="openEditProduct(product)"
                title="Edit"
              >
                <nb-icon icon="edit-outline"></nb-icon>
              </button>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm"
                (click)="deleteProduct(product)"
                title="Delete"
              >
                <nb-icon icon="trash-2-outline"></nb-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="empty-state" *ngIf="products.length === 0">
        <nb-card>
          <nb-card-body class="text-center py-5">
            <nb-icon icon="shopping-bag-outline" class="empty-icon"></nb-icon>
            <p class="text-muted mt-3">No products in the chain catalog yet.</p>
            <button nbButton status="primary" size="small" (click)="openAddProduct()">
              <nb-icon icon="plus-outline"></nb-icon> Create First Product
            </button>
          </nb-card-body>
        </nb-card>
      </div>
    </div>

    <div class="text-center py-4" *ngIf="productLoading">
      <nb-icon icon="loader-outline" class="spin-icon"></nb-icon>
      <span class="ms-2 text-muted">Loading products…</span>
    </div>
  </nb-tab>
</nb-tabset>

<ngx-spinner bdColor="rgba(51,51,51,0.8)" size="large" [fullScreen]="true" color="white" type="ball-circus">
  <p style="color: white">Loading…</p>
</ngx-spinner>
